import { createReducer, on } from '@ngrx/store';
import { templateDraftEntityAdapter as EntityAdapter, initialState } from './template-draft.state';
import { TemplateDraftActions as Actions } from './template-draft.actions';

export const templateDraftReducer = createReducer(
    initialState,

    // Load single
    on(Actions.loadSingle, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.loadSingleSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.upsertOne(entity, {
            ...state,
            isLoading: false,
            error: undefined
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.loadSingleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Load by category
    on(Actions.loadByCategory, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.loadByCategorySuccess, (state, { entities, correlationId }) => {
        const newState = EntityAdapter.setAll(entities, {
            ...state,
            isLoading: false,
            error: undefined
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entities }]
        };
    }),

    on(Actions.loadByCategoryFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Create
    on(Actions.create, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.createSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.addOne(entity, {
            ...state,
            isLoading: false,
            error: undefined,
            currentEntityId: entity.id
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.createFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Update
    on(Actions.update, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.updateSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.updateOne(
            { id: entity.id, changes: entity },
            {
                ...state,
                isLoading: false,
                error: undefined
            }
        );

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.updateFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Remove
    on(Actions.remove, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.removeSuccess, (state, { draftId, correlationId }) => {
        const newState = EntityAdapter.removeOne(draftId, {
            ...state,
            isLoading: false,
            error: undefined,
            currentEntityId: state.currentEntityId === draftId ? undefined : state.currentEntityId
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: draftId }]
        };
    }),

    on(Actions.removeFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Add templates - updates the draft's templates array
    on(Actions.addTemplates, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.addTemplatesSuccess, (state, { templates, correlationId }) => {
        // Templates are added on backend; we'll reload the draft to get updated data
        const newState = {
            ...state,
            isLoading: false,
            error: undefined
        };

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: templates }]
        };
    }),

    on(Actions.addTemplatesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Finalize
    on(Actions.finalize, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.finalizeSuccess, (state, { result, draftId, correlationId }) => {
        // Draft is deleted after finalization, so remove it from the store
        const newState = EntityAdapter.removeOne(draftId, {
            ...state,
            isLoading: false,
            error: undefined,
            currentEntityId: state.currentEntityId === draftId ? undefined : state.currentEntityId
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: result }]
        };
    }),

    on(Actions.finalizeFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Start edit
    on(Actions.startEdit, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.startEditSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.updateOne(
            { id: entity.id, changes: entity },
            {
                ...state,
                isLoading: false,
                error: undefined,
                currentEntityId: entity.id
            }
        );

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.startEditFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Edit template (create draft from existing)
    on(Actions.editTemplate, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.editTemplateSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.addOne(entity, {
            ...state,
            isLoading: false,
            error: undefined,
            currentEntityId: entity.id
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.editTemplateFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Edit multiple templates (create draft from existing)
    on(Actions.editTemplates, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.editTemplatesSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.addOne(entity, {
            ...state,
            isLoading: false,
            error: undefined,
            currentEntityId: entity.id
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.editTemplatesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Create order from existing templates
    on(Actions.createOrder, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.createOrderSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.addOne(entity, {
            ...state,
            isLoading: false,
            error: undefined,
            currentEntityId: entity.id
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.createOrderFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Edit selection (create draft from templates/bundles/categories)
    on(Actions.editSelection, (state) => ({
        ...state,
        isLoading: true
    })),

    on(Actions.editSelectionSuccess, (state, { entity, correlationId }) => {
        const newState = EntityAdapter.addOne(entity, {
            ...state,
            isLoading: false,
            error: undefined,
            currentEntityId: entity.id
        });

        if (!correlationId) return newState;

        return {
            ...newState,
            history: [...state.history, { correlationId, payload: entity }]
        };
    }),

    on(Actions.editSelectionFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error
    })),

    // Local state management
    on(Actions.setCurrentDraftId, (state, { draftId }) => ({
        ...state,
        currentEntityId: draftId
    })),

    on(Actions.setCurrentCategoryId, (state, { categoryId }) => ({
        ...state,
        currentCategoryId: categoryId
    })),

    on(Actions.clearStore, () => initialState)
);
